import {hashObject} from "@cm/utils/hashing"
import {ConfigInfo} from "#template/interface-descriptors"
import type {Parameters} from "#template/nodes/parameters"
import {
    type AnyInterfaceDescriptor,
    configurationFromDescriptors,
    configurationParts,
    type VariationPart,
    type VariationScope,
    variationName,
} from "#template/variations/configuration"

export type VariationEvaluation<Extra> = {
    descriptors: readonly AnyInterfaceDescriptor[]
    extra: Extra
}

export interface VariationOracle<Extra = undefined> {
    evaluate(parameters: Record<string, string>): Promise<VariationEvaluation<Extra>>
}

export type Variation<Extra = undefined> = {
    name: string
    parts: VariationPart[]
    parameters: Parameters
    hash: string
    extra: Extra
}

export type GatherVariationsOptions<Extra> = {
    onVariation: (variation: Variation<Extra>) => void
    onProgress?: (progress: number) => void
    onConfigInfo?: (configInfo: ConfigInfo) => void
    isAborted?: () => boolean
    yieldControl?: () => Promise<void>
}

const defaultYield = () => new Promise<void>((resolve) => setTimeout(resolve, 0))

// Reference semantics: the structural fast path must reproduce exactly what this finds.
export async function gatherAllVariations<Extra>(oracle: VariationOracle<Extra>, scope: VariationScope, options: GatherVariationsOptions<Extra>) {
    type PermutationProposal = Record<string, string>

    const {onVariation, onProgress, onConfigInfo, isAborted = () => false, yieldControl = defaultYield} = options
    const allConfigurations = new Set<string>()
    const visitedProposals = new Set<string>()
    const pendingProposals: PermutationProposal[] = [{}]
    let countUpperBound = 1

    onProgress?.(0)
    while (true) {
        if (isAborted()) break

        const proposal = pendingProposals.shift()
        if (!proposal) break

        let evaluation: VariationEvaluation<Extra>
        try {
            evaluation = await oracle.evaluate(proposal)
        } catch (e) {
            if (isAborted()) break
            throw e
        }

        const currentConfiguration = configurationFromDescriptors(evaluation.descriptors, scope === "global")
        const hash = currentConfiguration.getHash()

        if (!allConfigurations.has(hash)) {
            const descriptors = evaluation.descriptors.filter((d): d is ConfigInfo => d instanceof ConfigInfo)

            // A variant id carried over from the previous configuration can be missing from a context-sensitive (id-sharing) group.
            const hasInvalidProposedKey = descriptors.some((d) => proposal[d.props.id] !== undefined && d.props.value === null && !d.props.isSetByTemplate)

            const curConfigs: PermutationProposal = {}
            for (const descriptor of descriptors) {
                const {id, value, isSetByTemplate} = descriptor.props
                if (!value || isSetByTemplate) continue
                curConfigs[id] = value.id
            }

            if (!hasInvalidProposedKey) {
                const parts = configurationParts(descriptors)
                onVariation({
                    name: variationName(parts),
                    parts,
                    parameters: currentConfiguration.clone({cloneSubNode: () => true}),
                    hash,
                    extra: evaluation.extra,
                })
                allConfigurations.add(hash)
            }

            for (const descriptor of descriptors) {
                const {id, variants, isSetByTemplate} = descriptor.props

                if (isSetByTemplate) continue

                for (const variant of variants.filter((x) => !x.excludeFromPermutations)) {
                    const nextProposal: PermutationProposal = {
                        ...curConfigs,
                        [id]: variant.id,
                    }

                    const key = hashObject(nextProposal)
                    if (!visitedProposals.has(key)) {
                        countUpperBound += 1
                        visitedProposals.add(key)
                        pendingProposals.push(nextProposal)
                    }
                }
            }

            if (onConfigInfo) descriptors.forEach(onConfigInfo)

            await yieldControl()
        }

        onProgress?.(((countUpperBound - pendingProposals.length) / countUpperBound) * 100)
    }
}
